// Data hooks for the home board widgets (notes, countdowns, nightly
// checklist, weekly summary). One shared cache entry per key, with a saved
// snapshot so a reloaded wall shows the last copy at once; mutations
// update the cache optimistically and roll back on failure.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Countdown, CountdownInput, FamilyNote, NightlyPayload, NoteInput, WeekSummary } from "../../../shared/home";
import { api } from "../../data/api";
import { useSkyQuery } from "../sky/api";

const MIN = 60_000;

export const HOME_KEYS = {
  notes: ["home", "notes"] as const,
  countdowns: ["home", "countdowns"] as const,
  nightly: ["home", "nightly"] as const,
  summary: ["home", "summary"] as const,
};

export function useNotes() {
  return useSkyQuery<FamilyNote[]>(HOME_KEYS.notes, "/home/notes", { refetchMs: 2 * MIN, staleMs: MIN });
}

export function useNoteActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: HOME_KEYS.notes });
  return {
    save: useMutation({
      mutationFn: ({ id, input }: { id: string | null; input: NoteInput }) =>
        id ? api<FamilyNote>(`/home/notes/${id}`, { method: "PATCH", body: input }) : api<FamilyNote>("/home/notes", { method: "POST", body: input }),
      onSuccess: (n) => qc.setQueryData<FamilyNote[]>(HOME_KEYS.notes, (old) => [n, ...(old ?? []).filter((x) => x.id !== n.id)]),
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: (n: FamilyNote) => api(`/home/notes/${n.id}`, { method: "DELETE" }),
      onMutate: async (n) => {
        await qc.cancelQueries({ queryKey: HOME_KEYS.notes });
        const prev = qc.getQueryData<FamilyNote[]>(HOME_KEYS.notes);
        qc.setQueryData<FamilyNote[]>(HOME_KEYS.notes, (old) => (old ?? []).filter((x) => x.id !== n.id));
        return { prev };
      },
      onError: (_e, _n, ctx) => ctx?.prev && qc.setQueryData(HOME_KEYS.notes, ctx.prev),
      onSettled: refresh,
    }),
  };
}

export function useCountdowns() {
  return useSkyQuery<Countdown[]>(HOME_KEYS.countdowns, "/home/countdowns", { refetchMs: 15 * MIN, staleMs: 5 * MIN });
}

export function useCountdownActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: HOME_KEYS.countdowns });
  return {
    save: useMutation({
      mutationFn: ({ id, input }: { id: string | null; input: CountdownInput }) =>
        id ? api<Countdown>(`/home/countdowns/${id}`, { method: "PATCH", body: input }) : api<Countdown>("/home/countdowns", { method: "POST", body: input }),
      onSuccess: (cd) => qc.setQueryData<Countdown[]>(HOME_KEYS.countdowns, (old) => [...(old ?? []).filter((x) => x.id !== cd.id), cd]),
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: (cd: Countdown) => api(`/home/countdowns/${cd.id}`, { method: "DELETE" }),
      onSuccess: (_r, cd) => qc.setQueryData<Countdown[]>(HOME_KEYS.countdowns, (old) => (old ?? []).filter((x) => x.id !== cd.id)),
      onSettled: refresh,
    }),
  };
}

export function useNightly() {
  return useSkyQuery<NightlyPayload>(HOME_KEYS.nightly, "/home/nightly", { refetchMs: 2 * MIN, staleMs: MIN });
}

export function useNightlyActions() {
  const qc = useQueryClient();
  const put = (p: NightlyPayload) => qc.setQueryData(HOME_KEYS.nightly, p);
  return {
    check: useMutation({
      mutationFn: (v: { itemId: string; checked: boolean; date: string }) => api<NightlyPayload>("/home/nightly/check", { method: "PUT", body: v }),
      onMutate: async (v) => {
        await qc.cancelQueries({ queryKey: HOME_KEYS.nightly });
        const prev = qc.getQueryData<NightlyPayload>(HOME_KEYS.nightly);
        if (prev)
          put({
            ...prev,
            checks: v.checked ? [...prev.checks.filter((c) => c.itemId !== v.itemId), { itemId: v.itemId, checkedAt: new Date().toISOString(), checkedBy: null }] : prev.checks.filter((c) => c.itemId !== v.itemId),
          });
        return { prev };
      },
      onError: (_e, _v, ctx) => ctx?.prev && put(ctx.prev),
      onSuccess: (p, v) => {
        // A response for another night (the date rolled over) must not replace tonight's view.
        if (qc.getQueryData<NightlyPayload>(HOME_KEYS.nightly)?.date === v.date) put(p);
      },
    }),
    add: useMutation({ mutationFn: (v: { label: string; icon: string }) => api<NightlyPayload>("/home/nightly/items", { method: "POST", body: v }), onSuccess: put }),
    update: useMutation({ mutationFn: (v: { id: string; label: string; icon: string }) => api<NightlyPayload>(`/home/nightly/items/${v.id}`, { method: "PATCH", body: v }), onSuccess: put }),
    remove: useMutation({ mutationFn: (id: string) => api<NightlyPayload>(`/home/nightly/items/${id}`, { method: "DELETE" }), onSuccess: put }),
  };
}

export function useWeekSummary() {
  return useSkyQuery<WeekSummary>(HOME_KEYS.summary, "/home/summary", { refetchMs: 30 * MIN, staleMs: 10 * MIN });
}
